import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import * as Sentry from '@sentry/react-native';
import type {
    PricingCatalog,
    PricingCatalogFeature,
    PricingCatalogPlan,
    PricingPlanId,
} from './pricingCatalog';
import { resolveMessage } from './i18n/messages';

type CopyBlock = {
    name: MessageDescriptor;
    description: MessageDescriptor;
};

const catalogCopy = {
    title: msg({
        id: 'pricing.catalog.title',
        message: 'Pricing',
        comment: 'Public pricing page heading.',
    }),
    subtitle: msg({
        id: 'pricing.catalog.subtitle',
        message: 'Choose the plan that matches how many research projects you want Paperzilla to monitor.',
        comment: 'Public pricing page subtitle. A project is a monitored research topic.',
    }),
    annualSavingsLabel: msg({
        id: 'pricing.catalog.annualSavingsLabel',
        message: 'Save ~20%',
        comment: 'Short badge beside annual billing. Preserve the approximate sign.',
    }),
    taxNote: msg({
        id: 'pricing.catalog.taxNote',
        message: '+ applicable tax',
        comment: 'Short pricing disclosure shown after an amount.',
    }),
};

const planCopy: Record<PricingPlanId, CopyBlock & { badge?: MessageDescriptor }> = {
    starter: {
        name: msg({ id: 'pricing.plan.starter.name', message: 'Starter' }),
        description: msg({
            id: 'pricing.plan.starter.description',
            message: 'One project with paid-quality matching',
            comment: 'A project is a monitored research topic.',
        }),
    },
    plus: {
        name: msg({ id: 'pricing.plan.plus.name', message: 'Plus' }),
        description: msg({
            id: 'pricing.plan.plus.description',
            message: 'Five projects with private workspaces',
            comment: 'A project is a monitored research topic.',
        }),
    },
    pro: {
        name: msg({ id: 'pricing.plan.pro.name', message: 'Pro' }),
        description: msg({
            id: 'pricing.plan.pro.description',
            message: 'Ten projects, API access, and advanced discovery tools',
            comment: 'A project is a monitored research topic.',
        }),
        badge: msg({ id: 'pricing.plan.pro.badge', message: 'Best value' }),
    },
};

const featureCopy = {
    max_projects: {
        name: msg({ id: 'pricing.feature.maxProjects.name', message: 'Active Projects' }),
        description: msg({
            id: 'pricing.feature.maxProjects.description',
            message: 'Projects are ongoing research monitors. Each project tracks a topic, ranks new papers, and delivers updates for that research area.',
            comment: 'A project is an ongoing monitor for one research topic.',
        }),
    },
    base_source_access: {
        name: msg({ id: 'pricing.feature.baseSources.name', message: 'Base Sources' }),
        description: msg({
            id: 'pricing.feature.baseSources.description',
            message: 'Included in all plans: arXiv, medRxiv, bioRxiv, and IACR ePrint. Covers core preprint feeds across general science, medicine, biology, and cryptography so every project starts with useful source coverage right away.',
        }),
    },
    premium_source_access: {
        name: msg({ id: 'pricing.feature.premiumSources.name', message: 'Premium Sources' }),
        description: msg({
            id: 'pricing.feature.premiumSources.description',
            message: 'Adds ChemRxiv and ChinaXiv on top of the base sources. Useful for chemistry, adjacent life-science or materials research, and China-focused preprint monitoring.',
        }),
    },
    pubmed_source_access: {
        name: msg({ id: 'pricing.feature.pubmed.name', message: 'PubMed Source' }),
        description: msg({
            id: 'pricing.feature.pubmed.description',
            message: 'Adds PubMed-indexed biomedical and clinical literature to every paid plan. PubMed metadata and abstracts do not imply that the full paper is open access.',
        }),
    },
    patent_source_access: {
        name: msg({ id: 'pricing.feature.patents.name', message: 'Patent Sources' }),
        description: msg({
            id: 'pricing.feature.patents.description',
            message: 'Include patents alongside papers so R&D teams can monitor technical disclosures, prior art, and potential commercialization signals.',
        }),
    },
    source_on_demand: {
        name: msg({ id: 'pricing.feature.customSources.name', message: 'Custom Sources on Request' }),
        description: msg({
            id: 'pricing.feature.customSources.description',
            message: 'Request onboarding of additional sources for your research area or organization. Best for institution, enterprise, or specialized R&D workflows that need coverage beyond the standard source set.',
        }),
    },
    max_summaries_per_month: {
        name: msg({ id: 'pricing.feature.summaries.name', message: 'AI Summaries per Month' }),
        description: msg({
            id: 'pricing.feature.summaries.description',
            message: 'Monthly allowance for generating structured paper summaries, so you can quickly understand key claims, methods, limitations, and takeaways.',
        }),
    },
    matching_tier: {
        name: msg({ id: 'pricing.feature.matching.name', message: 'Higher-Accuracy Matching' }),
        description: msg({
            id: 'pricing.feature.matching.description',
            message: "Uses Paperzilla's stronger matching pipeline to rank papers more accurately for your project and reduce noisy, off-topic results.",
        }),
    },
    classification_model_tier: {
        name: msg({ id: 'pricing.feature.classificationModel.name', message: 'Classification Model Tier' }),
        description: msg({
            id: 'pricing.feature.classificationModel.description',
            message: 'Controls which LLM tier is used for relevance classification. Free uses Flash-Lite, Starter and Plus use Flash, and Pro uses the Pro model.',
            comment: 'Flash-Lite, Starter, Plus, Flash, Pro, and LLM are product/model names.',
        }),
    },
    delivery_bundle: {
        name: msg({ id: 'pricing.feature.delivery.name', message: 'Delivery' }),
        description: msg({
            id: 'pricing.feature.delivery.description',
            message: 'The delivery channels available for new matching papers in this plan.',
        }),
    },
    access_api: {
        name: msg({ id: 'pricing.feature.api.name', message: 'API Access' }),
        description: msg({
            id: 'pricing.feature.api.description',
            message: "Use Paperzilla's read/write API for custom integrations, automation, internal tools, and programmatic access to projects, feeds, papers, and summaries.",
        }),
    },
    access_forward_inbox: {
        name: msg({ id: 'pricing.feature.forwardInbox.name', message: 'Forwarded Alert Inbox' }),
        description: msg({
            id: 'pricing.feature.forwardInbox.description',
            message: 'Forward Google Scholar alerts, journal alerts, or other paper notification emails into Paperzilla so they can be parsed, filtered, and added to your workflow.',
        }),
    },
    has_recommender_system: {
        name: msg({ id: 'pricing.feature.recommender.name', message: 'Personalized Recommender' }),
        description: msg({
            id: 'pricing.feature.recommender.description',
            message: 'Learns from your feedback, such as Useful, Very useful, and Not for me, so future matches become better tuned to your interests.',
            comment: 'Useful, Very useful, and Not for me are feedback control labels.',
        }),
    },
    can_private_projects: {
        name: msg({ id: 'pricing.feature.privateProjects.name', message: 'Private Projects' }),
        description: msg({
            id: 'pricing.feature.privateProjects.description',
            message: 'Create projects that are not publicly visible by default. Useful for sensitive research interests, competitive scouting, client work, or team workflows.',
        }),
    },
    chat_feed: {
        name: msg({ id: 'pricing.feature.chatFeed.name', message: 'Chat With Your Feed' }),
        description: msg({
            id: 'pricing.feature.chatFeed.description',
            message: 'Ask questions across the papers in your feed, such as what changed this week, which papers matter most, or what themes are emerging.',
        }),
    },
    chat_paper: {
        name: msg({ id: 'pricing.feature.chatPaper.name', message: 'Chat With a Paper' }),
        description: msg({
            id: 'pricing.feature.chatPaper.description',
            message: 'Ask questions about an individual paper and get grounded answers from its content, including methods, results, limitations, and follow-up questions to investigate.',
        }),
    },
    full_text_relevance_matching: {
        name: msg({ id: 'pricing.feature.fullText.name', message: 'Full-Text Matching' }),
        description: msg({
            id: 'pricing.feature.fullText.description',
            message: 'Checks relevance against the full paper text when available, not just the title and abstract. Useful when key methods, datasets, or findings are buried deeper in the PDF.',
        }),
    },
    ocr_markdown_extraction: {
        name: msg({ id: 'pricing.feature.pdfExtraction.name', message: 'High-Quality PDF Extraction' }),
        description: msg({
            id: 'pricing.feature.pdfExtraction.description',
            message: 'Converts paper PDFs into cleaner, structured markdown using higher-quality OCR and extraction, improving summaries and making difficult PDFs easier to work with.',
        }),
    },
    summary_graph_chart_extraction: {
        name: msg({ id: 'pricing.feature.figures.name', message: 'Figures in Summaries' }),
        description: msg({
            id: 'pricing.feature.figures.description',
            message: "Includes important figures, graphs, and charts in summaries, so you can see the evidence behind a paper's results instead of only reading text.",
        }),
    },
    dataset_repo_discovery: {
        name: msg({ id: 'pricing.feature.datasets.name', message: 'Dataset & Code Discovery' }),
        description: msg({
            id: 'pricing.feature.datasets.description',
            message: 'Finds related datasets, code repositories, and project pages connected to papers, helping you move from what was published to what you can inspect, reproduce, or reuse.',
        }),
    },
    has_explainability: {
        name: msg({ id: 'pricing.feature.explainability.name', message: 'Why It Matched' }),
        description: msg({
            id: 'pricing.feature.explainability.description',
            message: 'Shows why Paperzilla considered a paper relevant to your project, including the topics, keywords, or signals that drove the match, so you can trust and refine the feed.',
        }),
    },
    export_zotero_notebooklm: {
        name: msg({ id: 'pricing.feature.export.name', message: 'Zotero & NotebookLM Export' }),
        description: msg({
            id: 'pricing.feature.export.description',
            message: 'Export papers and summaries into Zotero or NotebookLM so Paperzilla findings can move into your reference manager, reading workflow, or writing workflow.',
        }),
    },
    can_select_model: {
        name: msg({ id: 'pricing.feature.modelSelection.name', message: 'Model Selection' }),
        description: msg({
            id: 'pricing.feature.modelSelection.description',
            message: 'Choose the LLM used for supported workflows, balancing speed, cost, and depth depending on the research task.',
        }),
    },
} satisfies Record<string, CopyBlock>;

export const PRICING_FEATURE_IDS = Object.freeze(Object.keys(featureCopy).sort());

const fixedFeatureText: Partial<Record<keyof typeof featureCopy, MessageDescriptor>> = {
    base_source_access: msg({ id: 'pricing.feature.baseSources.text', message: 'Base sources: arXiv, medRxiv, bioRxiv, IACR ePrint' }),
    premium_source_access: msg({ id: 'pricing.feature.premiumSources.text', message: 'Premium sources: ChemRxiv, ChinaXiv' }),
    pubmed_source_access: msg({ id: 'pricing.feature.pubmed.text', message: 'PubMed-indexed literature' }),
    patent_source_access: msg({ id: 'pricing.feature.patents.text', message: 'Patent sources' }),
    source_on_demand: msg({ id: 'pricing.feature.customSources.text', message: 'Custom sources on request' }),
    matching_tier: msg({ id: 'pricing.feature.matching.text', message: 'LLM-powered classification' }),
    delivery_bundle: msg({ id: 'pricing.feature.delivery.text', message: 'Delivery: in-app feed, digest emails, RSS/Atom, CLI, MCP' }),
    access_api: msg({ id: 'pricing.feature.api.text', message: 'Full read/write API access' }),
    access_forward_inbox: msg({ id: 'pricing.feature.forwardInbox.text', message: 'Forward / Inbox' }),
    has_recommender_system: msg({ id: 'pricing.feature.recommender.text', message: 'Recommender system' }),
    can_private_projects: msg({ id: 'pricing.feature.privateProjects.text', message: 'Private projects' }),
    chat_feed: msg({ id: 'pricing.feature.chatFeed.text', message: 'Chat with your feed' }),
    chat_paper: msg({ id: 'pricing.feature.chatPaper.text', message: 'Chat with paper' }),
    full_text_relevance_matching: msg({ id: 'pricing.feature.fullText.text', message: 'Full text relevance matching' }),
    ocr_markdown_extraction: msg({ id: 'pricing.feature.pdfExtraction.text', message: 'High-quality OCR markdown extraction' }),
    summary_graph_chart_extraction: msg({ id: 'pricing.feature.figures.text', message: 'Graph/chart extraction in summaries' }),
    dataset_repo_discovery: msg({ id: 'pricing.feature.datasets.text', message: 'Dataset/repo discovery' }),
    has_explainability: msg({ id: 'pricing.feature.explainability.text', message: 'Explainability: why relevant' }),
    export_zotero_notebooklm: msg({ id: 'pricing.feature.export.text', message: 'Export to Zotero / NotebookLM' }),
    can_select_model: msg({ id: 'pricing.feature.modelSelection.text', message: 'Model selection where supported' }),
};

const reportedUnknownKeys = new Set<string>();

function reportUnknownKey(kind: 'plan' | 'feature', id: string): void {
    const key = `${kind}:${id}`;
    if (reportedUnknownKeys.has(key)) return;
    reportedUnknownKeys.add(key);
    Sentry.captureMessage('Pricing catalog copy key is not localized', {
        level: 'warning',
        tags: {
            pricing_copy_kind: kind,
            pricing_copy_id: id,
        },
    });
}

function featureText(
    i18n: I18n,
    feature: PricingCatalogFeature,
    plan: PricingCatalogPlan,
): string | null {
    if (feature.id === 'max_projects') {
        return resolveMessage(i18n, msg({
            id: 'pricing.feature.maxProjects.text',
            message: '{count, plural, one {# project/topic} other {# projects/topics}}',
        }), { count: Number(feature.value) });
    }
    if (feature.id === 'max_summaries_per_month') {
        return resolveMessage(i18n, msg({
            id: 'pricing.feature.summaries.text',
            message: '{count, plural, one {# summary generation / month} other {# summary generations / month}}',
        }), { count: Number(feature.value) });
    }
    if (feature.id === 'classification_model_tier') {
        return i18n._(plan.id === 'pro'
            ? msg({ id: 'pricing.feature.classificationModel.proText', message: 'Pro relevance model' })
            : msg({ id: 'pricing.feature.classificationModel.flashText', message: 'Fast Flash relevance model' }));
    }
    const descriptor = fixedFeatureText[feature.id as keyof typeof featureCopy];
    return descriptor ? i18n._(descriptor) : feature.text;
}

export function localizePricingCatalog(i18n: I18n, catalog: PricingCatalog): PricingCatalog {
    return {
        ...catalog,
        copy: {
            title: i18n._(catalogCopy.title),
            subtitle: i18n._(catalogCopy.subtitle),
            annual_savings_label: i18n._(catalogCopy.annualSavingsLabel),
            tax_note: i18n._(catalogCopy.taxNote),
        },
        plans: catalog.plans.map((plan) => {
            const localizedPlan = planCopy[plan.id];
            if (!localizedPlan) {
                reportUnknownKey('plan', plan.id);
                return plan;
            }
            return {
                ...plan,
                name: i18n._(localizedPlan.name),
                description: i18n._(localizedPlan.description),
                badge: localizedPlan.badge ? i18n._(localizedPlan.badge) : plan.badge,
                features: plan.features.map((feature) => {
                    const localizedFeature = featureCopy[feature.id as keyof typeof featureCopy];
                    if (!localizedFeature) {
                        reportUnknownKey('feature', feature.id);
                        return feature;
                    }
                    return {
                        ...feature,
                        name: i18n._(localizedFeature.name),
                        description: i18n._(localizedFeature.description),
                        text: featureText(i18n, feature, plan),
                    };
                }),
            };
        }),
    };
}
